//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { Grid, makeStyles } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logPageView } from '../system/firebase';
import { ELASTICSEARCH_SORT_OPTIONS, ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC } from '../common/elasticSearchHelper';
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { setClassNameInDefns } from '../common/definitionHelper';
import { AppContext, LayoutContext } from '../common/contexts';
import { getNewJobCenterQuery } from '../common/queryHelper';
import { BASE_URL, PAGE_TITLE_JOB_CENTER } from '../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { loadRecentSearches } from '../common/services/jobCenterServices';
// import { loadAdvertisementsByPage } from '../common/services/adServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import BrandPageDialog from '../empBrandPage/subComponents/BrandPageDialog';
import TopNav from '../topNav/TopNav';
import AfitdAdContainer from '../common/elemental/AfitdAdContainer';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import Header from './subComponents/Header';
import SideBar from './subComponents/Sidebar';
import FiltersBar from './subComponents/FilterBar';
import JobDetail from './subComponents/JobDetail';
import Footer from './subComponents/Footer';
import SearchInput from './subComponents/SearchInput';
import AfitdFab from '../common/elemental/AfitdFab';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
// import _isArray from 'lodash/isArray';
import _filter from 'lodash/filter';
import _omit from 'lodash/omit';
import _isEqual from 'lodash/isEqual';
import { PATH_JOB_CENTER } from '../system/constants';
// import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { searchedPerformed } = params;

    return makeStyles(theme => ({
        jobCenterContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
        jobCenterHeader: { maxHeight: '81px' },
        jobCenterContent: {
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingLeft: '64px',
            paddingRight: '64px',
            [theme.breakpoints.only('md')]: {
                // overflowY: 'scroll',
                paddingLeft: '52px',
                paddingRight: '52px',
            },
            [theme.breakpoints.down('sm')]: {
                // overflowY: 'scroll',
                paddingLeft: '0px',
                paddingRight: '0px',
            },

            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },
        jobCenterLeft: {
            height: '100%',
            paddingTop: '25px',
            [theme.breakpoints.down('lg')]: {
                height: searchedPerformed ? '100%' : 'fit-content',
            },
        },
        jobCenterRight: {
            overflowY: 'hidden',
            flexWrap: 'unset',
            paddingTop: '35px',
            // [theme.breakpoints.down('md')]: { minHeight: '600px' },
            [theme.breakpoints.up('xl')]: { height: '100%' }, // this makes 1440px viewport only the detail section scrollable
            [theme.breakpoints.down('lg')]: { paddingTop: '10px' },
        },
        jobFilterContainer: {
            paddingLeft: '64px',
            paddingRight: '64px',
            [theme.breakpoints.only('md')]: {
                paddingLeft: '52px',
                paddingRight: '52px',
            },
            [theme.breakpoints.down('sm')]: {
                paddingLeft: '15px',
                paddingRight: '15px',
            },
        },
        jobFilterLeft: { paddingTop: '25px', [theme.breakpoints.down('sm')]: { paddingTop: '10px' } },
        jobFilterRight: { paddingTop: '28px', paddingLeft: 14, [theme.breakpoints.down('lg')]: { paddingTop: '10px' } },
        navigation: { padding: '16px 0px 16px 20px', [theme.breakpoints.down('md')]: { padding: '16px 0px 16px 0px' } },
        navigationButtonContainer: { display: 'flex', alignItems: 'center' },
        navigationIconContainer: {
            width: '32px',
            height: '32px',
            background: '#F2F2F4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { cursor: 'pointer' },
        },
        navIconLeft: { marginLeft: 11, width: 18 },
        navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
        bottomFooterContainer: { [theme.breakpoints.up('md')]: { paddingLeft: '64px', paddingRight: '64px' } },
        adContainerGrid: {
            // overflowY: 'hidden',
            height: 'calc(100% - 80px)', //'100%',
            flexWrap: 'unset',
            paddingTop: '35px',
            // minHeight: '600px',
            [theme.breakpoints.down('lg')]: { paddingTop: '0px' },
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        handleChangeSearchResultPageNumber,
        sortTag,
        setSortTag,
        handleDeleteChip,
        selectedJob,
        setSelectedJob,
        query,
        setQuery,
        isDefaultSearch,
        brandPageDialogInfo,
        // setBrandPageDialogInfo,
        handleCloseBrandPageDialog,
        setShowDetails,
        showFilterPage,
        setShowFilterPage,
        isBrandPage,
        showAds,
        setShowAds,
        searchedPerformed,
        setSearchPerformed,
        handleClickJob,
        smMatches,
        xlMatches,
        showDetails,
        handleClickGoToTopButton,
    } = params;

    const brandPageDialogOpen = _get(brandPageDialogInfo, 'open');
    const brandPageEmployerId = _get(brandPageDialogInfo, 'employerId');

    const goToTopButtonIsHidden = xlMatches || !(selectedJob || (!showAds && showDetails));

    const defns = {
        jobCenterContainer: { container: true, direction: 'column' },
        jobCenterHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        jobCenterContent: { item: true, container: true, justify: 'center' },
        jobCenterLeft: { item: true, xl: 4, lg: 12, md: 12, sm: 12, xs: 12 },
        jobCenterRight: { item: true, container: true, direction: 'column', xl: 8, lg: 12, md: 12, sm: 11, xs: 11 },
        jobFilterContainer: { container: true, justifyContent: 'center', alignItems: 'flex-start' },
        jobFilterLeft: { item: true, xl: 6, lg: 12, md: 12, sm: 12, xs: 12 },
        jobFilterRight: { item: true, xl: 6, lg: 12, md: 12, sm: 11, xs: 11 },
        navigation: { container: true, direction: 'row' },
        navigationItem: { item: true, xl: 3, lg: 4, md: 11, sm: 12, xs: 12 },
        bottomFooterContainer: { container: true, direction: 'row', justify: 'center' },
        bottomFooter: { item: true, xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },

        container: {
            maxWidth: 'xl',
        },
        root: {
            container: true,
            spacing: 2,
        },
        sidebarContainer: {
            item: true,
            lg: 3,
            md: 12,
            sm: 12,
            xs: 12,
        },
        adContainerGrid: {
            item: true,
            container: true,
            direction: 'column',
            xl: 8,
            lg: 12,
            md: 12,
            sm: 12,
            xs: 12,
        },
        adContainer: {
            isDefaultSearch,
        },
        contentContainer: {
            item: true,
            lg: 9,
            md: 12,
            sm: 12,
            xs: 12,
        },
        sidebar: {
            queryInitState,
            selectedJob,
            setSelectedJob,
            onPageChange: handleChangeSearchResultPageNumber,
            onClickItem: handleClickJob,
            sortTag,
            setSortTag,
            onDeleteChip: handleDeleteChip,
            query,
            setQuery,
            setShowDetails,
            showFilterPage,
            setShowFilterPage,
            showAds,
            setShowAds,
            searchedPerformed,
            setSearchPerformed,
        },
        filterBar: {
            queryInitState,
            query,
            setQuery,
            setShowAds,
            setShowFilterPage,
        },
        jobDetail: {
            job: selectedJob,
        },
        brandPageDialog: {
            open: brandPageDialogOpen,
            onClose: handleCloseBrandPageDialog,
            employerId: brandPageEmployerId,
        },
        searchInput: {
            isHidden: isBrandPage,
            query,
            setQuery,
            setSortTag,
        },
        goToTopButton: {
            isHidden: goToTopButtonIsHidden,
            size: 'small',
            style: {
                position: 'absolute',
                right: smMatches ? 10 : 30,
                bottom: smMatches ? 80 : 60,
            },
            onClick: handleClickGoToTopButton,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};
// const timeInterval = minutesToMilliseconds(30);

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const queryInitState = {
    jobType: [],
    datePost: null,
    salaryRate: null,
    workFromHome: false,
    internPositions: false,
    jobTitle: null,
    location: null,
    //TODO Remove this when we are not loading all jobs on page load
    sortTag: ELASTICSEARCH_SORT_OPTIONS[ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC],
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobCenterLayout = () => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    // const { esToken, setEsToken } = appContext;
    const { userId } = appContext;

    const layoutContext = useContext(LayoutContext);

    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    const theme = useTheme();
    const xlMatches = useMediaQuery(theme.breakpoints.up('xl'));
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const history = useHistory();

    const { jobId: paramJobId, employerId: paramEmployerId } = useParams();
    const location = useLocation();
    const locationJobId = _get(location, 'state.state.jobId');
    const jobId = paramJobId || locationJobId;
    const singleJobMode = !!jobId;

    // derive the first query as the initial state of 'query' (only if jobId is passed in as router param/state)
    let firstQuery = queryInitState;
    if (jobId) {
        firstQuery = { id: jobId };
    } else if (paramEmployerId) {
        firstQuery = { employerId: paramEmployerId };
    }

    const myRef = useRef(null);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    //TODO Change this back to ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT when we are not loading all jobs on page load
    const [sortTag, setSortTag] = useState(ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC);
    // const [sortTag, setSortTag] = useState(ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT);
    const [selectedJob, setSelectedJob] = useState(null);
    const [brandPageDialogInfo, setBrandPageDialogInfo] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showFilterPage, setShowFilterPage] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);
    const [showAds, setShowAds] = useState(true);
    const [searchedPerformed, setSearchPerformed] = React.useState(false);
    const [hideSidebar, setHideSideBar] = React.useState(false);
    const [advertisements] = React.useState([]);
    const [isRequestOpenLoginDialog, setIsRequestOpenLoginDialog] = useState(false);
    const [asyncCallCount, setAsyncCallCount] = React.useState(0);

    const [query, setQuery] = useState(firstQuery);
    const [searchResultTotal, setSearchResultTotal] = useState();

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const brandPageDialogOpen = _get(brandPageDialogInfo, 'open');
    const isDefaultSearch = _isEqual(_omit(query, ['sortTag']), _omit(queryInitState, ['sortTag']));

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_JOB_CENTER);
    }, []);

    useEffect(() => {
        if (singleJobMode && !_isEqual(query, firstQuery)) history.push(`${BASE_URL}${PATH_JOB_CENTER}`);
    }, [singleJobMode, query, firstQuery, history]);

    // useEffect(() => {
    //     loadAdvertisementsByPage({ jobCtr: true, callBack: onServiceCallBack });
    //     setInterval(() => {
    //         loadAdvertisementsByPage({ jobCtr: true, callBack: onServiceCallBack });
    //     }, timeInterval);
    // }, []);

    // load recent searches
    useEffect(() => {
        let unsubscribeLoadRecentSearches = null;
        if (userId) {
            unsubscribeLoadRecentSearches = loadRecentSearches({ userId, setData: setRecentSearches });
        }
        return () => {
            unsubscribeLoadRecentSearches && unsubscribeLoadRecentSearches();
        };
    }, [userId]);

    // determine if the left pane should be hidden based on the viewport size
    useEffect(() => {
        if (xlMatches) {
            setHideSideBar(false);
        } else if (lgMatches) {
            if (selectedJob || (!showAds && showDetails)) {
                setHideSideBar(true);
            }
        }
    }, [xlMatches, lgMatches, selectedJob, showAds, showDetails]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleDeleteChip = (key, value) => {
        switch (key) {
            case 'jobType': {
                setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { jobType: _filter(prevQuery.jobType, item => item !== value) } }));
                return;
            }
            case 'datePost': {
                setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { datePost: null } }));
                return;
            }
            case 'salaryRate': {
                setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { salaryRate: null } }));
                return;
            }
            case 'workFromHome': {
                setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { workFromHome: false } }));
                return;
            }
            case 'internPositions': {
                setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { internPositions: false } }));
                return;
            }
            default:
            // do nothing
        }
    };

    const handleCloseBrandPageDialog = () => {
        setBrandPageDialogInfo(null);
    };

    const handleChangeSearchResultPageNumber = newPageNumber => {
        // setCurrentPageNumber(newPageNumber);
    };

    const handleClickBackArrow = () => {
        setShowDetails(false);
        setHideSideBar(false);
        setSelectedJob(null);
    };

    const handleClickJob = () => {
        if (lgMatches) {
            setHideSideBar(true);
        }
        setShowAds(false);
        setShowDetails(true);
    };

    // const onServiceCallBack = data => {
    //     setAdvertisements(data);
    // };

    const handleClickGoToTopButton = () => {
        // goToTop( contentWrapperEl, contentEl)
        setTimeout(() => {
            myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ searchedPerformed })();

    const defns = getDefns({
        classes,
        handleChangeSearchResultPageNumber,
        sortTag,
        setSortTag,
        handleDeleteChip,
        selectedJob,
        setSelectedJob,
        query,
        setQuery,
        isDefaultSearch,
        brandPageDialogInfo,
        setBrandPageDialogInfo,
        handleCloseBrandPageDialog,
        setShowDetails,
        showFilterPage,
        setShowFilterPage,
        isBrandPage: layoutContext?.isBrandPage,
        showAds,
        setShowAds,
        searchedPerformed,
        setSearchPerformed,
        handleClickJob,
        smMatches,
        xlMatches,
        showDetails,
        handleClickGoToTopButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...layoutContext,
                isJobCenter: true,
                recentSearches,
                setRecentSearches,
                brandPageDialogInfo,
                setBrandPageDialogInfo,
                brandPageDialogOnClose: handleCloseBrandPageDialog,
                isRequestOpenLoginDialog,
                setIsRequestOpenLoginDialog,
                searchResultTotal,
                setSearchResultTotal,
                setAsyncCallCount,
            }}
        >
            <AfitdPageHead title={PAGE_TITLE_JOB_CENTER} />
            <Grid {...defns.jobCenterContainer}>
                {smMatches && showFilterPage ? null : (
                    <Grid {...defns.jobCenterHeader}>
                        <TopNav isShowProgressBar={asyncCallCount > 0} />
                    </Grid>
                )}
                <Grid {...defns.jobFilterContainer}>
                    {/* {smMatches && showFilterPage ? null : ( */}
                    <Grid {...defns.jobFilterLeft}>
                        <SearchInput {...defns.searchInput} />
                    </Grid>
                    {/* )} */}
                    {!smMatches && (
                        <Grid {...defns.jobFilterRight}>
                            <FiltersBar {...defns.filterBar} />
                        </Grid>
                    )}
                </Grid>
                <Grid {...defns.jobCenterContent}>
                    {/* {showDetails ? (
                        <Grid {...defns.jobCenterRight}>
                            <Grid {...defns.navigation}>
                                <Grid {...defns.navigationItem}>
                                    <div {...defns.navigationButtonContainer} onClick={() => setShowDetails(false)}>
                                        <div {...defns.navigationIconContainer}>
                                            <ArrowBackIosIcon {...defns.navIconLeft} />
                                        </div>
                                        <span {...defns.navigationText}>Back</span>
                                    </div>
                                </Grid>
                            </Grid>
                            <JobDetail {...defns.jobDetail} />
                        </Grid>
                    ) : (
                    )} */}
                    {!hideSidebar && (
                        <Grid {...defns.jobCenterLeft}>
                            <SideBar {...defns.sidebar} />
                        </Grid>
                    )}

                    {showAds && !selectedJob ? (
                        lgMatches ? null : (
                            <Grid {...defns.adContainerGrid}>
                                {/* -----------------------------Display Ads Container---------------------------------- */}
                                <AfitdAdContainer {...defns.adContainer} data={advertisements} searchResultTotal={searchResultTotal} isJobCenter singleJobMode={singleJobMode} />
                                {/* -----------------------------Display Ads Container---------------------------------- */}
                            </Grid>
                        )
                    ) : (
                        (selectedJob || (!showAds && showDetails)) && (
                            <Grid {...defns.jobCenterRight}>
                                <div ref={myRef}></div>
                                {(smMatches || mdMatches || lgMatches) && (
                                    <Grid {...defns.navigation}>
                                        <Grid {...defns.navigationItem}>
                                            <div {...defns.navigationButtonContainer} onClick={handleClickBackArrow}>
                                                <div {...defns.navigationIconContainer}>
                                                    <ArrowBackIosIcon {...defns.navIconLeft} />
                                                </div>
                                                <span {...defns.navigationText}>Back</span>
                                            </div>
                                        </Grid>
                                    </Grid>
                                )}
                                <JobDetail {...defns.jobDetail} />
                            </Grid>
                        )
                    )}
                </Grid>
                {smMatches && showFilterPage ? null : (
                    <Grid {...defns.bottomFooterContainer}>
                        <Grid {...defns.bottomFooter}> {lgMatches ? <Footer /> : null}</Grid>
                    </Grid>
                )}
            </Grid>
            {brandPageDialogOpen && <BrandPageDialog {...defns.brandPageDialog} />}
            <AfitdFab aria-label="Go to the Top" {...defns.goToTopButton}>
                <ArrowUpwardIcon />
            </AfitdFab>
        </LayoutContext.Provider>
    );
};

export default JobCenterLayout;
